import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft,
  Loader2,
  MapPin,
  Bed,
  Bath,
  Maximize2,
  Star,
  Check,
  X,
  Home,
  Shield,
  Dog,
  Video,
  Heart,
  Scale
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ComparePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get listing IDs from URL params
  const listingIds = searchParams.get('ids')?.split(',') || [];

  useEffect(() => {
    if (listingIds.length >= 2) {
      fetchComparison();
    } else {
      setError('Select 2-4 properties to compare');
      setLoading(false);
    }
  }, [searchParams]);

  const fetchComparison = async () => {
    try {
      const idsParam = listingIds.map(id => `listing_ids=${id}`).join('&');
      const response = await fetch(`${API_URL}/api/compare-properties?${idsParam}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProperties(data.properties || []);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to load comparison');
      }
    } catch (error) {
      console.error('Failed to fetch comparison:', error);
      setError('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const removeProperty = (id) => {
    const newIds = listingIds.filter(lid => lid !== id);
    if (newIds.length >= 2) {
      navigate(`/compare?ids=${newIds.join(',')}`);
    } else {
      navigate('/favorites');
    }
  };

  // Common amenities to compare
  const amenityList = [
    { key: 'wifi', label: 'WiFi', icon: '📶' },
    { key: 'parking', label: 'Parking', icon: '🅿️' },
    { key: 'washer', label: 'Washer', icon: '🧺' },
    { key: 'dryer', label: 'Dryer', icon: '👕' },
    { key: 'dishwasher', label: 'Dishwasher', icon: '🍽️' },
    { key: 'ac', label: 'Air Conditioning', icon: '❄️' },
    { key: 'heating', label: 'Heating', icon: '🔥' },
    { key: 'balcony', label: 'Balcony', icon: '🌇' },
    { key: 'garden', label: 'Garden', icon: '🌳' },
    { key: 'gym', label: 'Gym Access', icon: '💪' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-signal-blue" />
      </div>
    );
  }

  if (error || properties.length < 2) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
            <Scale className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">
              {error || t('compare.not_enough', 'Not Enough Properties')}
            </h2>
            <p className="text-slate-600 mb-6">
              {t('compare.select_properties', 'Select 2-4 properties from your favorites to compare them side by side.')}
            </p>
            <Button
              onClick={() => navigate('/favorites')}
              className="bg-signal-blue hover:bg-signal-blue-hover text-white"
            >
              <Heart className="w-4 h-4 mr-2" />
              {t('compare.go_to_favorites', 'Go to Favorites')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="compare-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-slate-600 hover:text-slate-200 mb-2 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.back', 'Back')}
            </Button>
            <h1 className="font-heading text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Scale className="w-8 h-8 text-signal-blue" />
              {t('compare.title', 'Compare Properties')}
            </h1>
            <p className="text-slate-600 mt-1">
              {t('compare.comparing', 'Comparing')} {properties.length} {t('compare.properties_side', 'properties side by side')}
            </p>
          </div>
          
          <Link to="/favorites">
            <Button variant="outline" className="border-slate-300 text-slate-300">
              {t('compare.add_more', 'Add More')}
            </Button>
          </Link>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {/* Property Cards Row */}
            <div className={`grid gap-4 mb-6`} style={{ gridTemplateColumns: `repeat(${properties.length}, minmax(250px, 1fr))` }}>
              {properties.map((prop) => (
                <div key={prop.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  {/* Image */}
                  <div className="relative aspect-[16/10] bg-slate-800">
                    {prop.hero_photo_url ? (
                      <img
                        src={prop.hero_photo_url}
                        alt={prop.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="w-12 h-12 text-slate-600" />
                      </div>
                    )}
                    <button
                      onClick={() => removeProperty(prop.id)}
                      className="absolute top-2 right-2 w-6 h-6 bg-white/80 rounded-full flex items-center justify-center text-slate-600 hover:text-red-400 hover:bg-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {prop.base_amount > 0 && (
                      <Badge className="absolute bottom-2 right-2 bg-military-gold text-slate-950">
                        €{prop.base_amount.toLocaleString()}/mo
                      </Badge>
                    )}
                  </div>
                  
                  {/* Title */}
                  <div className="p-4">
                    <Link to={`/listings/${prop.id}`}>
                      <h3 className="font-bold text-slate-800 hover:text-signal-blue transition-colors line-clamp-1">
                        {prop.title}
                      </h3>
                    </Link>
                    <div className="flex items-center gap-1 text-slate-600 text-sm mt-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {prop.location_name}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparison Rows */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 divide-y divide-slate-800">
              {/* Price */}
              <CompareRow label="Monthly Rent" properties={properties}>
                {(prop) => (
                  <span className="text-lg font-bold text-military-gold">
                    €{prop.base_amount?.toLocaleString() || 'N/A'}
                  </span>
                )}
              </CompareRow>

              {/* Bedrooms */}
              <CompareRow label="Bedrooms" icon={<Bed className="w-4 h-4" />} properties={properties}>
                {(prop) => <span>{prop.bedrooms || 0}</span>}
              </CompareRow>

              {/* Bathrooms */}
              <CompareRow label="Bathrooms" icon={<Bath className="w-4 h-4" />} properties={properties}>
                {(prop) => <span>{prop.bathrooms || 0}</span>}
              </CompareRow>

              {/* Size */}
              <CompareRow label="Size (sqm)" icon={<Maximize2 className="w-4 h-4" />} properties={properties}>
                {(prop) => <span>{prop.sqm ? `${prop.sqm} m²` : 'N/A'}</span>}
              </CompareRow>

              {/* Rating */}
              <CompareRow label="Rating" icon={<Star className="w-4 h-4" />} properties={properties}>
                {(prop) => (
                  <div className="flex items-center gap-1">
                    {prop.avg_rating > 0 ? (
                      <>
                        <Star className="w-4 h-4 text-military-gold fill-current" />
                        <span>{prop.avg_rating}</span>
                        <span className="text-slate-800">({prop.review_count})</span>
                      </>
                    ) : (
                      <span className="text-slate-800">No reviews</span>
                    )}
                  </div>
                )}
              </CompareRow>

              {/* TLA Status */}
              <CompareRow label="TLA Status" icon={<Shield className="w-4 h-4" />} properties={properties}>
                {(prop) => (
                  <Badge className={
                    prop.tla_status === 'eligible' 
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                      : 'bg-slate-700 text-slate-600'
                  }>
                    {prop.tla_status === 'eligible' ? 'TLA Eligible' : prop.tla_status || 'Unknown'}
                  </Badge>
                )}
              </CompareRow>

              {/* Pets */}
              <CompareRow label="Pet Policy" icon={<Dog className="w-4 h-4" />} properties={properties}>
                {(prop) => (
                  <span className={prop.pet_policy?.toLowerCase().includes('allowed') || prop.pet_policy?.toLowerCase().includes('yes') ? 'text-emerald-400' : 'text-slate-600'}>
                    {prop.pet_policy || 'Ask owner'}
                  </span>
                )}
              </CompareRow>

              {/* Virtual Tour */}
              <CompareRow label="Virtual Tour" icon={<Video className="w-4 h-4" />} properties={properties}>
                {(prop) => (
                  prop.virtual_tour_url ? (
                    <Check className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <X className="w-5 h-5 text-slate-600" />
                  )
                )}
              </CompareRow>

              {/* Amenities */}
              {amenityList.map((amenity) => (
                <CompareRow 
                  key={amenity.key} 
                  label={amenity.label} 
                  icon={<span className="text-sm">{amenity.icon}</span>}
                  properties={properties}
                >
                  {(prop) => {
                    const hasAmenity = prop.amenities?.some(a => 
                      a.toLowerCase().includes(amenity.key.toLowerCase()) ||
                      amenity.label.toLowerCase().includes(a.toLowerCase())
                    );
                    return hasAmenity ? (
                      <Check className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <X className="w-5 h-5 text-slate-600" />
                    );
                  }}
                </CompareRow>
              ))}
            </div>

            {/* View Buttons */}
            <div className={`grid gap-4 mt-6`} style={{ gridTemplateColumns: `repeat(${properties.length}, minmax(250px, 1fr))` }}>
              {properties.map((prop) => (
                <Link key={prop.id} to={`/listings/${prop.id}`}>
                  <Button className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white">
                    View Details
                  </Button>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component for comparison rows
function CompareRow({ label, icon, properties, children }) {
  return (
    <div className={`grid items-center`} style={{ gridTemplateColumns: `180px repeat(${properties.length}, minmax(250px, 1fr))` }}>
      <div className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-slate-600">
        {icon}
        {label}
      </div>
      {properties.map((prop) => (
        <div key={prop.id} className="px-4 py-3 text-sm text-slate-200">
          {children(prop)}
        </div>
      ))}
    </div>
  );
}
